import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { createFranchiseAdmin } from '../../../services/franchise.service';
import { State, City } from 'country-state-city';
import { maharashtraData } from '../../../utils/maharashtraData';

const FranchiseAdminRegistration = () => {
    const indianStates = State.getStatesOfCountry('IN');
    const [availableCities, setAvailableCities] = useState([]);
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        password: '',
        mobile: '',
        state: '',
        district: '',
        city: '',
        officeAddress: '',
        pincode: '',
        accountHolderName: '',
        bankName: '',
        accountNumber: '',
        ifscCode: ''
    });
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    
    // Dropdown states
    const [showStateDropdown, setShowStateDropdown] = useState(false);
    const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);
    const [showCityDropdown, setShowCityDropdown] = useState(false);

    useEffect(() => {
        const selectedState = indianStates.find(s => s.name === formData.state);
        if (selectedState) {
            setAvailableCities(City.getCitiesOfState('IN', selectedState.isoCode));
        } else {
            setAvailableCities([]);
        }
    }, [formData.state]);

    const handleChange = (e) => {
        let { name, value } = e.target;
        
        // Restrict to numbers only for specific fields
        if (name === 'pincode') {
            value = value.replace(/\D/g, '').slice(0, 6);
        } else if (name === 'mobile') {
            value = value.replace(/\D/g, '').slice(0, 10);
        } else if (name === 'accountNumber') {
            value = value.replace(/\D/g, '');
        }

        setFormData({
            ...formData,
            [name]: value
        });
    };

    // Filters for dropdowns
    const filteredStates = indianStates.filter(s => s.name.toLowerCase().includes(formData.state.toLowerCase()));
    
    let filteredDistricts = [];
    let filteredCities = [];

    if (formData.state === 'Maharashtra') {
        const mhDistricts = Object.keys(maharashtraData);
        filteredDistricts = mhDistricts
            .filter(d => d.toLowerCase().includes(formData.district.toLowerCase()))
            .map(d => ({ name: d })); // Format as {name: 'District'}
            
        if (formData.district && maharashtraData[formData.district]) {
            filteredCities = maharashtraData[formData.district]
                .filter(c => c.toLowerCase().includes(formData.city.toLowerCase()))
                .map(c => ({ name: c }));
        }
    } else {
        filteredDistricts = availableCities.filter(c => c.name.toLowerCase().includes(formData.district.toLowerCase()));
        filteredCities = availableCities.filter(c => c.name.toLowerCase().includes(formData.city.toLowerCase()));
    }

    const nextStep = () => {
        // Simple validation before next step
        if (currentStep === 1) {
            if (!formData.fullName || !formData.email || !formData.password || !formData.mobile) {
                return Swal.fire('Warning', 'Please fill all required fields in Step 1', 'warning');
            }
        } else if (currentStep === 2) {
            if (!formData.state || !formData.district || !formData.city || !formData.officeAddress || !formData.pincode) {
                return Swal.fire('Warning', 'Please fill all required fields in Step 2', 'warning');
            }
        }
        setCurrentStep(prev => prev + 1);
    };

    const prevStep = () => {
        setCurrentStep(prev => prev - 1);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (currentStep !== 3) return;

        if (!formData.accountHolderName || !formData.bankName || !formData.accountNumber || !formData.ifscCode) {
            return Swal.fire('Warning', 'Please fill all bank details in Step 3', 'warning');
        }

        setLoading(true);
        
        try {
            await createFranchiseAdmin(formData);

            Swal.fire({
                title: 'Success!',
                text: 'Franchise Admin registered successfully.',
                icon: 'success',
                confirmButtonColor: '#1a7a5e'
            });

            // Reset form
            setFormData({
                fullName: '', email: '', password: '', mobile: '',
                state: '', district: '', city: '', officeAddress: '', pincode: '',
                accountHolderName: '', bankName: '', accountNumber: '', ifscCode: ''
            });
            setCurrentStep(1);
        } catch (error) {
            Swal.fire({
                title: 'Error!',
                text: error.message || 'Something went wrong while registering.',
                icon: 'error',
                confirmButtonColor: '#1a7a5e'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="admin-content-v2">
            <div className="admin-header-v2">
                <div>
                    <h1 className="admin-title-v2">Franchise Admin Registration</h1>
                    <p className="admin-subtitle-v2">Register a new franchise admin to the system</p>
                </div>
            </div>

            <div className="franchise-form-card">
                <div className="step-indicator">
                    <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>1. Personal Info</div>
                    <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>2. Location Info</div>
                    <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>3. Bank Details</div>
                </div>

                <form onSubmit={handleSubmit}>
                    {currentStep === 1 && (
                        <div className="form-grid">
                            <div className="form-group">
                                <label>Full Name *</label>
                                <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} required placeholder="Enter full name" />
                            </div>
                            <div className="form-group">
                                <label>Email Address *</label>
                                <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="Enter email address" />
                            </div>
                            <div className="form-group">
                                <label>Password *</label>
                                <div className="password-wrapper">
                                    <input 
                                        type={showPassword ? "text" : "password"} 
                                        name="password" 
                                        value={formData.password} 
                                        onChange={handleChange} 
                                        required 
                                        placeholder="Create a password"
                                    />
                                    <button 
                                        type="button" 
                                        className="password-toggle"
                                        onClick={() => setShowPassword(!showPassword)}
                                        title={showPassword ? "Hide password" : "Show password"}
                                    >
                                        {showPassword ? (
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                                        ) : (
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Mobile Number *</label>
                                <input type="tel" name="mobile" value={formData.mobile} onChange={handleChange} required placeholder="Enter 10-digit mobile number" pattern="[0-9]{10}" />
                            </div>
                        </div>
                    )}

                    {currentStep === 2 && (
                        <div className="form-grid">
                            <div className="form-group custom-dropdown-container">
                                <label>State *</label>
                                <input 
                                    type="text" 
                                    name="state" 
                                    value={formData.state} 
                                    onChange={handleChange} 
                                    onFocus={() => setShowStateDropdown(true)}
                                    onBlur={() => setTimeout(() => setShowStateDropdown(false), 200)}
                                    required 
                                    placeholder="Enter State" 
                                    autoComplete="off"
                                />
                                {showStateDropdown && filteredStates.length > 0 && (
                                    <ul className="custom-dropdown-list">
                                        {filteredStates.map(s => (
                                            <li key={s.isoCode} onClick={() => {
                                                setFormData({ ...formData, state: s.name, district: '', city: '' });
                                                setShowStateDropdown(false);
                                            }}>{s.name}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div className="form-group custom-dropdown-container">
                                <label>District *</label>
                                <input 
                                    type="text" 
                                    name="district" 
                                    value={formData.district} 
                                    onChange={handleChange} 
                                    onFocus={() => setShowDistrictDropdown(true)}
                                    onBlur={() => setTimeout(() => setShowDistrictDropdown(false), 200)}
                                    required 
                                    placeholder="Enter District" 
                                    autoComplete="off"
                                />
                                {showDistrictDropdown && filteredDistricts.length > 0 && (
                                    <ul className="custom-dropdown-list">
                                        {filteredDistricts.map((c, idx) => (
                                            <li key={`dist-${idx}`} onClick={() => {
                                                setFormData({ ...formData, district: c.name, city: '' });
                                                setShowDistrictDropdown(false);
                                            }}>{c.name}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div className="form-group custom-dropdown-container">
                                <label>City *</label>
                                <input 
                                    type="text" 
                                    name="city" 
                                    value={formData.city} 
                                    onChange={handleChange} 
                                    onFocus={() => setShowCityDropdown(true)}
                                    onBlur={() => setTimeout(() => setShowCityDropdown(false), 200)}
                                    required 
                                    placeholder="Enter City" 
                                    autoComplete="off"
                                />
                                {showCityDropdown && filteredCities.length > 0 && (
                                    <ul className="custom-dropdown-list">
                                        {filteredCities.map((c, idx) => (
                                            <li key={`city-${idx}`} onClick={() => {
                                                setFormData({ ...formData, city: c.name });
                                                setShowCityDropdown(false);
                                            }}>{c.name}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div className="form-group">
                                <label>Pincode *</label>
                                <input type="text" name="pincode" value={formData.pincode} onChange={handleChange} required placeholder="Enter Pincode" pattern="[0-9]{6}" />
                            </div>
                            <div className="form-group full-width">
                                <label>Office Address *</label>
                                <textarea name="officeAddress" value={formData.officeAddress} onChange={handleChange} required placeholder="Enter complete office address" rows="3"></textarea>
                            </div>
                        </div>
                    )}

                    {currentStep === 3 && (
                        <div className="form-grid">
                            <div className="form-group">
                                <label>Account Holder Name *</label>
                                <input type="text" name="accountHolderName" value={formData.accountHolderName} onChange={handleChange} required placeholder="Enter account holder name" />
                            </div>
                            <div className="form-group">
                                <label>Bank Name *</label>
                                <input type="text" name="bankName" value={formData.bankName} onChange={handleChange} required placeholder="Enter bank name" />
                            </div>
                            <div className="form-group">
                                <label>Account Number *</label>
                                <input type="text" name="accountNumber" value={formData.accountNumber} onChange={handleChange} required placeholder="Enter account number" />
                            </div>
                            <div className="form-group">
                                <label>IFSC Code *</label>
                                <input type="text" name="ifscCode" value={formData.ifscCode} onChange={handleChange} required placeholder="Enter IFSC code" />
                            </div>
                        </div>
                    )}

                    <div className="form-actions" style={{ justifyContent: currentStep > 1 ? 'space-between' : 'flex-end' }}>
                        {currentStep > 1 && (
                            <button type="button" className="btn-secondary" onClick={prevStep}>
                                Back
                            </button>
                        )}
                        {currentStep < 3 ? (
                            <button type="button" className="submit-btn" onClick={nextStep}>
                                Next Step
                            </button>
                        ) : (
                            <button type="submit" className="submit-btn" disabled={loading}>
                                {loading ? 'Registering...' : 'Register Franchise Admin'}
                            </button>
                        )}
                    </div>
                </form>
            </div>

            <style>{`
                .admin-content-v2 { padding: 2rem; }
                .franchise-form-card {
                    background: white;
                    padding: 2.5rem;
                    border-radius: 12px;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.05);
                    max-width: 900px;
                }
                .form-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1.5rem;
                }
                .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }
                .form-group.full-width {
                    grid-column: 1 / -1;
                }
                .form-group label {
                    font-size: 0.9rem;
                    font-weight: 600;
                    color: #1e293b;
                }
                .form-group input, .form-group textarea {
                    padding: 0.75rem 1rem;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    font-size: 0.95rem;
                    transition: all 0.2s;
                }
                .form-group input:focus, .form-group textarea:focus {
                    outline: none;
                    border-color: #1a7a5e;
                    box-shadow: 0 0 0 3px rgba(26, 122, 94, 0.1);
                }
                .password-wrapper {
                    position: relative;
                    display: flex;
                    align-items: center;
                }
                .password-wrapper input {
                    width: 100%;
                    padding-right: 40px;
                }
                .password-toggle {
                    position: absolute;
                    right: 10px;
                    background: none;
                    border: none;
                    cursor: pointer;
                    color: #64748b;
                    padding: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .password-toggle:hover {
                    color: #1e293b;
                }
                .password-toggle svg {
                    width: 18px;
                    height: 18px;
                }
                
                /* Custom Dropdown Styles */
                .custom-dropdown-container {
                    position: relative;
                }
                .custom-dropdown-list {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    margin-top: 4px;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
                    max-height: 200px;
                    overflow-y: auto;
                    z-index: 50;
                    list-style: none;
                    padding: 0;
                }
                .custom-dropdown-list li {
                    padding: 10px 15px;
                    cursor: pointer;
                    font-size: 0.95rem;
                    color: #1e293b;
                    border-bottom: 1px solid #f1f5f9;
                    transition: background 0.2s;
                }
                .custom-dropdown-list li:last-child {
                    border-bottom: none;
                }
                .custom-dropdown-list li:hover {
                    background: #f8fafc;
                    color: #1a7a5e;
                }
                
                .form-actions {
                    margin-top: 2rem;
                    display: flex;
                    justify-content: flex-end;
                }
                .submit-btn {
                    background: #1a7a5e;
                    color: white;
                    border: none;
                    padding: 0.8rem 2rem;
                    border-radius: 8px;
                    font-weight: 600;
                    font-size: 1rem;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .submit-btn:hover {
                    background: #135c47;
                }
                .submit-btn:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }
                .btn-secondary {
                    background: #f1f5f9;
                    color: #475569;
                    border: none;
                    padding: 0.8rem 2rem;
                    border-radius: 8px;
                    font-weight: 600;
                    font-size: 1rem;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .btn-secondary:hover {
                    background: #e2e8f0;
                    color: #1e293b;
                }
                .step-indicator {
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 2rem;
                    border-bottom: 2px solid #f1f5f9;
                    padding-bottom: 1rem;
                }
                .step {
                    color: #94a3b8;
                    font-weight: 600;
                    font-size: 0.95rem;
                    flex: 1;
                    text-align: center;
                    position: relative;
                }
                .step.active {
                    color: #1a7a5e;
                }
                .step.active::after {
                    content: '';
                    position: absolute;
                    bottom: -18px;
                    left: 0;
                    width: 100%;
                    height: 4px;
                    background: #1a7a5e;
                    border-radius: 2px;
                }
                @media (max-width: 768px) {
                    .form-grid { grid-template-columns: 1fr; }
                    .step-indicator { flex-direction: column; gap: 1rem; border-bottom: none; }
                    .step.active::after { display: none; }
                }
            `}</style>
        </div>
    );
};

export default FranchiseAdminRegistration;
